import React, { useEffect, useState } from "react";
import { Button, Input, Spinner, VStack, HStack } from "@chakra-ui/react";
import { useForm } from "@/uses/useForm";
import { useApi } from "@/uses/useApi";
import { Field, FileInput, FilesInput, LanguageField, RichText } from "@/components";
import { useLanguage } from "@/contexts/languageContext";
import RessourcesInput from "components/input/RessourcesInput";

export const CreateOrEditPartner = (props) => {
  const partnerId = props?.id;

  const { t } = useLanguage();
  const [state, setState] = useState(-1);
  const { getLangObject } = useLanguage();
  const [partner, setPartner] = useState(null);
  const [defaultLanguage, setDefaultLanguage] = useState("fr");
  const api = useApi();
  const {
    formData,
    formFieldErrors,
    handleInputChange,
    handleInputChanges,
    handleFormSubmit,
  } = useForm(
    {
      title: "",
      url: "",
      logo: null,
      ressources: [],
      description: getLangObject(),
    },
    {
      title: {
        required: {
          value: true,
          message: t("errors.pleaseFillField"),
        },
      },
      description: {
        custom: {
          function: (value) => {
            return Object.values(value).some((v) => v && v.length >= 3);
          },
          message: t("errors.pleaseFillFieldAllLanguages"),
        },
      },
      ressources: {
        custom: {
          function: (value) => {
            if (!value || value.length === 0) return true;
            return value.every((r) => r.name && r.type && r.content);
          },
          message: t("errors.pleaseFillAllResources"),
        },
      },
    },
  );

  useEffect(() => {
    setState(() => 0);
    if (partnerId) {
      api.ApiService.getOne("partner", partnerId, {
        attributes: ["id", "title", "url", "logo", "ressources", "description"],
      }).then((res) => {
        if (res?.data?.type === "success" && res?.data?.value) {
          setPartner(() => res.data.value);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (partner !== null) {
      handleInputChanges({
        ...partner,
        description: partner.description ?? getLangObject(),
      });
      setDefaultLanguage(partner.description?.fr?.length > 0 ? "fr" : "en");
    }
    if (!partnerId || partner !== null) {
      setState(() => 1);
    }
  }, [partner, partnerId]);

  return (
    <VStack h="100%" overflow="hidden" p="1.5rem 0" gap="5vh" w="100%">
      {state === 1 ? (
        <VStack
          overflow="auto"
          flex={1}
          gap="5"
          p="5"
          w="100%"
          align="stretch"
          justify="flex-start"
        >
          <Field
            label={t('wrappers.mandatoryLabel', { value: t('fields.title') })}
            errors={formFieldErrors.title}
          >
            <Input
              value={formData.title}
              onChange={(e) => {
                handleInputChange("title", e?.target?.value ?? "");
              }}
              placeholder={t("partners.partnerTitle")}
            />
          </Field>
          <Field
            label={t('wrappers.nonMandatoryLabel', { value: t('fields.url') })}
            errors={formFieldErrors.url}
          >
            <Input
              value={formData.url}
              onChange={(e) => {
                handleInputChange("url", e?.target?.value ?? "");
              }}
              placeholder={t("partners.partnerUrl")}
              type="url"
            />
          </Field>
          <LanguageField
            id="description"
            errors={formFieldErrors.description}
            label={t('wrappers.mandatoryLabel', { value: t('fields.description') })}
            defaultLanguage={defaultLanguage}
            renderElement={(lang) => (
              <RichText
                initialValue={formData.description?.[lang] ?? ""}
                onChange={(e) => {
                  handleInputChange("description", {
                    ...formData.description,
                    [lang]: e,
                  });
                }}
                placeholder={t("partners.partnerDescription")}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  minHeight: '150px',
                  overflow: 'hidden'
                }}
              />
            )}
          />
          <HStack>
            <Field
              label={t('wrappers.nonMandatoryLabel', { value: t('fields.logo') })}
              errors={formFieldErrors.logo}
            >
              <FileInput
                accept="image/*"
                file={formData.logo}
                hasPreview
                alt={formData.title}
                onChange={(file) =>
                  handleInputChange(
                    "logo",
                    typeof file === "function" ? file() : file,
                  )
                }
                minH="15vw"
                maxH="15vw"
                minW="15vw"
                maxW="15vw"
              />
            </Field>
            <RessourcesInput
              ressources={formData.ressources}
              onChange={(ressources) => handleInputChange("ressources", ressources)}
              error={formFieldErrors.ressources}
            />
          </HStack>
        </VStack>
      ) : (
        <VStack flex={1} w="100%" align="center" justify="flex-start">
          <Spinner size="lg" />
        </VStack>
      )}
      <Button
        variant="primary"
        mt="auto"
        height="auto"
        borderRadius=".5rem"
        style={{ padding: "1rem 1.5rem" }}
        zIndex={10}
        position="relative"
        onClick={() =>
          handleFormSubmit(async (data) => {
            if (partnerId) {
              return api.ApiService.update("partner", partnerId, data);
            }
            return api.ApiService.create("partner", data);
          }, props?.successCallback)
        }
      >
        {t("actions.validate")}
      </Button>
    </VStack>
  );
};

export default CreateOrEditPartner;
